import React, { useEffect, useRef, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Box, Card, Chip, Typography, IconButton, Stack, Tooltip } from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import PlaceIcon from '@mui/icons-material/Place'
import GroupChip from './GroupChip'
import WishlistMenu from './WishlistMenu'
import { toggleWishlist, toggleVisit } from '../lib/locationInteractions'
import { WishlistSummary } from '../lib/wishlists'
import { getCategoryImage } from '../lib/categoryImages'

interface GroupRef {
  id: number
  name: string
}

interface LocationLike {
  id: number
  slug?: string | null
  name: string
  address?: string | null
  city?: string | null
  country?: string | null
  category?: string | null
  thumbnail_url?: string | null
  google_place_id?: string | null
  groups?: GroupRef[]
}

// Module-level cache so Google photos for the same place_id are only fetched once
// per page load (and shared across LocationCard instances).
const googlePhotoCache: Record<string, string | null> = {}
const googlePhotoPromises: Record<string, Promise<string | null>> = {}

function fetchGooglePhoto(placeId: string): Promise<string | null> {
  if (googlePhotoCache[placeId] !== undefined) {
    return Promise.resolve(googlePhotoCache[placeId])
  }
  if (googlePhotoPromises[placeId]) return googlePhotoPromises[placeId]

  const p = fetch(`/api/v1/google_maps/photos/${encodeURIComponent(placeId)}?max_width=320&limit=1`, {
    headers: { Accept: 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : Promise.reject(res.statusText)))
    .then((data) => {
      const url = (data.photos || [])[0] || null
      googlePhotoCache[placeId] = url
      return url
    })
    .catch(() => {
      googlePhotoCache[placeId] = null
      return null
    })

  googlePhotoPromises[placeId] = p
  return p
}

interface Props {
  location: LocationLike
  layout?: 'horizontal' | 'vertical'
  showGroups?: boolean
  showActions?: boolean
  wishlistNames?: string[]
}

interface SharedInteractionsProps {
  current_user: { id: number } | null
  interactions?: { wishlisted_ids: number[]; visited_ids: number[] }
  wishlists_summary?: WishlistSummary[]
}

const categoryLabels: Record<string, string> = {
  restaurant: 'Restaurant',
  cafe: 'Cafe',
  filming_location: 'Filming Location',
  practice_studio: 'Practice Studio',
  entertainment_venue: 'Entertainment Venue',
  shop: 'Shop',
  landmark: 'Landmark',
  other: 'Other',
}

export default function LocationCard({ location, layout = 'horizontal', showGroups = true, showActions = true, wishlistNames }: Props) {
  const page = usePage<SharedInteractionsProps>()
  const signedIn = !!page.props.current_user
  const initialWishlisted = page.props.interactions?.wishlisted_ids?.includes(location.id) ?? false
  const initialVisited = page.props.interactions?.visited_ids?.includes(location.id) ?? false

  const [wishlisted, setWishlisted] = useState(initialWishlisted)
  const [visited, setVisited] = useState(initialVisited)
  const [busyWish, setBusyWish] = useState(false)
  const [busyVisit, setBusyVisit] = useState(false)
  const [googlePhoto, setGooglePhoto] = useState<string | null>(
    location.google_place_id ? googlePhotoCache[location.google_place_id] ?? null : null,
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const [localWishlists, setLocalWishlists] = useState<WishlistSummary[]>(page.props.wishlists_summary || [])
  const menuAnchorRef = useRef<HTMLButtonElement>(null)
  const isVertical = layout === 'vertical'

  useEffect(() => { setLocalWishlists(page.props.wishlists_summary || []) }, [page.props.wishlists_summary])

  useEffect(() => { setWishlisted(initialWishlisted) }, [initialWishlisted])
  useEffect(() => { setVisited(initialVisited) }, [initialVisited])

  const onOpenMenu = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation()
    if (!signedIn) return
    setMenuOpen(true)
  }

  const onToggleWishFromMenu = async () => {
    if (!signedIn || busyWish) return
    const next = !wishlisted
    setWishlisted(next); setBusyWish(true)
    const result = await toggleWishlist(location.id, next)
    if (result) { setWishlisted(result.wishlisted); setVisited(result.visited) }
    else setWishlisted(!next)
    setBusyWish(false)
  }

  const onToggleVisit = async (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation()
    if (!signedIn || busyVisit) return
    const next = !visited
    setVisited(next); setBusyVisit(true)
    const result = await toggleVisit(location.id, next)
    if (result) { setWishlisted(result.wishlisted); setVisited(result.visited) }
    else setVisited(!next)
    setBusyVisit(false)
  }

  useEffect(() => {
    if (location.thumbnail_url) return
    if (!location.google_place_id) return
    if (googlePhotoCache[location.google_place_id] !== undefined) {
      setGooglePhoto(googlePhotoCache[location.google_place_id])
      return
    }
    let cancelled = false
    fetchGooglePhoto(location.google_place_id).then((url) => {
      if (!cancelled) setGooglePhoto(url)
    })
    return () => { cancelled = true }
  }, [location.thumbnail_url, location.google_place_id])

  const imageUrl = location.thumbnail_url || googlePhoto
  const categoryFallback = imageUrl ? null : getCategoryImage(location.category)
  const displayImage = imageUrl || categoryFallback
  const thumbBg: React.CSSProperties = displayImage
    ? {
        backgroundImage: `url(${displayImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : {}

  return (
    <>
    <Card
      component={Link}
      href={`/locations/${location.slug || location.id}`}
      sx={{
        display: 'flex',
        flexDirection: isVertical ? 'column' : 'row',
        alignItems: 'stretch',
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 18px rgba(123, 91, 214,0.16)',
        },
      }}
    >
      <Box
        sx={{
          width: isVertical ? '100%' : 120,
          height: isVertical ? 140 : 'auto',
          alignSelf: 'stretch',
          flexShrink: 0,
          backgroundColor: 'rgba(123, 91, 214,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          ...thumbBg,
        }}
      >
        {!displayImage && <PlaceIcon sx={{ fontSize: isVertical ? 48 : 32 }} />}
      </Box>

      <Box sx={{ flexGrow: 1, p: isVertical ? 2 : 1.5, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              display: 'block',
              mb: 0.25,
            }}
          >
            {location.name}
          </Typography>
          {showGroups && location.groups && location.groups.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mb: 0.5, gap: 0.5 }}>
              {location.groups.slice(0, 3).map((g) => (
                <GroupChip key={g.id} group={g} />
              ))}
            </Stack>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {location.category && (
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600, mr: 0.75 }}>
                {categoryLabels[location.category] || location.category}
              </Box>
            )}
            {location.address || '—'}
          </Typography>
          {wishlistNames && wishlistNames.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5, gap: 0.5 }}>
              {wishlistNames.map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  icon={<FavoriteIcon sx={{ fontSize: 12 }} />}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    backgroundColor: 'rgba(123, 91, 214, 0.12)',
                    color: 'primary.main',
                    '& .MuiChip-icon': { color: 'primary.main', ml: 0.5 },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
        {showActions && signedIn && (
          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            <Tooltip title={visited ? 'Unmark as visited' : 'Mark as visited'}>
              <IconButton
                size="small"
                aria-label={visited ? 'unmark location as visited' : 'mark location as visited'}
                onClick={onToggleVisit}
                disabled={busyVisit}
                sx={{ color: visited ? 'success.main' : 'text.secondary' }}
              >
                {visited ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title={wishlisted ? 'Saved — manage lists' : 'Save to list'}>
              <IconButton
                ref={menuAnchorRef}
                size="small"
                aria-label={wishlisted ? 'saved location, manage lists' : 'save location to list'}
                onClick={onOpenMenu}
                disabled={busyWish}
                sx={{ color: 'primary.main' }}
              >
                {wishlisted ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>
    </Card>
    {/* Render the WishlistMenu as a SIBLING of Card (not a child) so React
        synthetic events from inside the portaled Popover do not bubble up
        through the <Card component={Link}> wrapper and trigger navigation. */}
    {showActions && signedIn && (
      <WishlistMenu
        anchorEl={menuAnchorRef.current}
        open={menuOpen}
        onClose={() => { setMenuOpen(false); router.reload({ only: ['wishlists_summary', 'wishlists'] }) }}
        locationId={location.id}
        wishlists={localWishlists}
        onWishlistsChange={(next) => {
          setLocalWishlists(next)
          // Adding to a list auto-wishlists; reflect locally.
          if (next.some((w) => w.location_ids.includes(location.id))) setWishlisted(true)
        }}
        wishlisted={wishlisted}
        onToggleWishlisted={onToggleWishFromMenu}
      />
    )}
    </>
  )
}
